import { forwardRef, useCallback, useImperativeHandle, useState } from 'react'
import { clsx } from 'clsx'
import type { SloganKind } from '../types'
import { SPLASH_COLORS } from '../lib/splash'

export interface SplashHandle {
  /** Fire a comic burst. `at` is viewport percentages (0–100); omit for random. */
  burst: (text: string, kind: SloganKind, at?: { x: number; y: number }) => void
}

interface Burst {
  id: number
  text: string
  color: string
  x: number
  y: number
  rot: number
  big: boolean
}

let seq = 0

function NeonSplash({ text, color, x, y, rot, big }: Burst) {
  return (
    <div
      className="animate-splash absolute pointer-events-none"
      style={{ left: `${x}%`, top: `${y}%`, ['--rot' as string]: `${rot}deg` }}
    >
      <div
        className={clsx(
          'grid place-items-center rounded-xl border px-3 font-comic uppercase tracking-[1.5px] shadow-neon-pink',
          big ? 'text-4xl py-1' : 'text-2xl py-0.5'
        )}
        style={{
          color: '#fff',
          background: 'rgba(10,10,16,0.65)',
          borderColor: color,
          boxShadow: `0 0 0 1px ${color}, 0 0 18px ${color}`,
          transform: `rotate(${rot}deg)`,
        }}
      >
        {text}
      </div>
    </div>
  )
}

export const SplashLayer = forwardRef<SplashHandle>(function SplashLayer(_props, ref) {
  const [bursts, setBursts] = useState<Burst[]>([])

  const burst = useCallback<SplashHandle['burst']>((text, kind, at) => {
    const id = ++seq
    const x = at?.x ?? 25 + Math.random() * 50
    const y = at?.y ?? 28 + Math.random() * 34
    const rot = -14 + Math.random() * 28
    const big = kind === 'milestone' || kind === 'combo'
    setBursts((b) => [...b, { id, text, color: SPLASH_COLORS[kind], x, y, rot, big }])
    window.setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 850)
  }, [])

  useImperativeHandle(ref, () => ({ burst }), [burst])

  return (
    <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden">
      {bursts.map((b) => (
        <NeonSplash key={b.id} {...b} />
      ))}
    </div>
  )
})
